/* global FB */

require('./multi-factor');
require('./feedback');

const pluginName = 'authentication';
const defaults = {
  i18n: {
    emptyPasswordOption: 'Please indicate whether or not you have a Race Roster password',
    terms: 'You must agree to the Terms of Service before creating a Race Roster account',
    enterPassword: 'Enter password',
    createPassword: 'Create password',
    signIn: 'Sign in',
    signUp: 'Sign up',
    redirecting: 'Redirecting…',
    multiFactorApp: 'Please enter your two-factor authentication code',
    multiFactorPhone: 'Please enter the verification code sent to your phone',
  },
  routes: {
    signUp: '/signup',
    signIn: '/signin',
    facebook: '/signin/vendor/facebook',
    oneAsics: '/signin/vendor/one-asics',
    runkeeper: '/signin/vendor/runkeeper',
    recoveryQuestions: '/recovery-questions',
  },
  oneAsicsSignInRequired: false,
  runkeeperSignInRequired: false,
  oneAsicsStyle: '',
};
const classes = {
  hidden: 'd-none',
  disabled: 'disabled',
};
const selectors = {
  form: '[data-js-authentication-form]',
  fieldset: '[data-js-authentication-form-fieldset]',
  formSubmit: '[data-js-authentication-form-submit]',
  mainFields: '[data-js-authentication-main-fields]',
  email: '[data-js-authentication-email]',
  action: '[data-js-authentication-action]',
  actionSignUp: '[data-js-authentication-action-sign-up]',
  actionSignIn: '[data-js-authentication-action-sign-in]',
  passwordLabel: '[data-js-authentication-password-label]',
  passwordHelpText: '[data-js-authentication-password-help-text]',
  forgotPassword: '[data-js-forgot-password]',
  termsContainer: '[data-js-authentication-terms-container]',
  termsCheckbox: '[data-js-authentication-terms-checkbox]',
  feedback: '[data-js-authentication-feedback]',
  facebook: '[data-js-authentication-facebook]',
  oneAsics: '[data-js-authentication-one-asics]',
  redirectToDashboard: '[data-redirect-to-dashboard]',
  reset: '[data-js-authentication-reset]',
  recoveryQuestions: '[data-js-authentication-recovery-questions]',
  multiFactorContainer: '[data-js-authentication-multi-factor-container]',
  otpWidgetContainer: '.js-otp-widget-container',
  modal: '[data-js-authentication-modal]',
  authTrigger: '[data-auth="true"]',
  authenticationOr: '[data-js-authenticator-or]',
};

/**
 * Get a nice little package of elements
 * @param $element
 * @returns {Object}
 */
const getElements = ($element) => ({
  $container: $element,
  $form: $element.find(selectors.form),
  $fieldset: $element.find(selectors.fieldset),
  $formSubmit: $element.find(selectors.formSubmit),
  $mainFields: $element.find(selectors.mainFields),
  $email: $element.find(selectors.email),
  $action: $element.find(selectors.action),
  $actionSignUp: $element.find(selectors.actionSignUp),
  $actionSignIn: $element.find(selectors.actionSignIn),
  $passwordLabel: $element.find(selectors.passwordLabel),
  $passwordHelpText: $element.find(selectors.passwordHelpText),
  $forgotPassword: $element.find(selectors.forgotPassword),
  $termsContainer: $element.find(selectors.termsContainer),
  $termsCheckbox: $element.find(selectors.termsCheckbox),
  $feedback: $element.find(selectors.feedback),
  $facebook: $element.find(selectors.facebook),
  $oneAsics: $element.find(selectors.oneAsics),
  $reset: $element.find(selectors.reset),
  $recoveryQuestions: $element.find(selectors.recoveryQuestions),
  $multiFactorContainer: $element.find(selectors.multiFactorContainer),
  $otpWidgetContainer: $element.find(selectors.otpWidgetContainer),
  $modal: $(selectors.modal),
  $authTrigger: $(selectors.authTrigger),
  $authenticationOr: $element.find(selectors.authenticationOr),
});

/**
 * Returns the plugin instance data for the element.
 * @param {jQuery} $element The plugin instance element.
 */
const getData = ($element) => $element.data(pluginName);

/**
 * Show and hide different things based on whether the user is signing up or in
 * @param data
 */
const changeAuthenticationAction = (data) => {
  const $action = data.elements.$action.filter(':checked');

  if (!$action.length) {
    return;
  }

  if ($action.is(selectors.actionSignUp)) {
    data.elements.$formSubmit.text(data.settings.i18n.signUp);
    data.elements.$passwordLabel.text(data.settings.i18n.createPassword);
    data.elements.$passwordHelpText.removeClass(classes.hidden);
    data.elements.$forgotPassword.addClass(classes.hidden);
    data.elements.$termsContainer.removeClass(classes.hidden);
    data.elements.$termsCheckbox.prop('required', true);
  } else {
    data.elements.$formSubmit.text(data.settings.i18n.signIn);
    data.elements.$passwordLabel.text(data.settings.i18n.enterPassword);
    data.elements.$passwordHelpText.addClass(classes.hidden);
    data.elements.$forgotPassword.removeClass(classes.hidden);
    data.elements.$termsContainer.addClass(classes.hidden);
    data.elements.$termsCheckbox.prop('required', false);
  }
};

/**
 * If data-redirect exists, return that, if action exists, return that, if redirecting to dashboard
 * is specifically required, return that, otherwise null
 * @param data
 * @returns {string|null}
 */
const getRedirectLocationFromForm = (data) => {
  const redirectString = data.elements.$form.data('redirect');

  if (redirectString) {
    return redirectString;
  }

  const action = data.elements.$form.attr('action');

  if (action) {
    return action;
  }

  if (data.elements.$container.is(selectors.redirectToDashboard)) {
    return '/dashboard';
  }

  return null;
};

/**
 * Send Google Analytics data on sign up and login
 * @param data
 * @param method
 */
const sendGAEvent = (data, method = 'Race Roster') => {
  const $action = data.elements.$action.filter(':checked');

  if ($action.is(selectors.actionSignUp)) {
    window.dataLayer.push({
      event: 'sign_up',
      method,
    });
  }

  if ($action.is(selectors.actionSignIn)) {
    window.dataLayer.push({
      event: 'login',
      method,
    });
  }
};

/**
 * The login was good, redirect based on a bunch of different things
 * @param data
 */
const succeedAuthentication = (data, response) => {
  // Handle password resetting
  if (response && response.email) {
    data.elements.$form.data('redirect', `/require-password-reset?email=${response.email}`);
  }

  // If the response contains a direct redirect instruction, set it
  if (response && response.redirect) {
    data.elements.$form.data('redirect', response.redirect);
  }

  const redirectString = getRedirectLocationFromForm(data);

  data.elements.$feedback.authenticationFeedback('provide', data.settings.i18n.redirecting, 'success');

  sendGAEvent(data, 'Race Roster');

  if (redirectString) {
    window.location.href = redirectString;
    return;
  }

  window.location.reload();
};

/**
 * Reset the form back to its original state
 * @param data
 */
const reset = (data) => {
  data.elements.$reset.addClass(classes.hidden);
  data.elements.$recoveryQuestions.addClass(classes.hidden);
  data.elements.$multiFactorContainer.multiFactor('disable');
  data.elements.$mainFields.removeClass(classes.hidden);
  data.elements.$forgotPassword.removeClass(classes.hidden);

  data.elements.$oneAsics.removeClass(classes.hidden);
  data.elements.$feedback.authenticationFeedback('clear');

  data.elements.$email.trigger('focus');
};

const switchToMultiFactorMode = (data, multiFactorData) => {
  data.elements.$reset.removeClass(classes.hidden);
  data.elements.$recoveryQuestions.removeClass(classes.hidden);
  data.elements.$mainFields.addClass(classes.hidden);
  data.elements.$forgotPassword.addClass(classes.hidden);
  data.elements.$facebook.addClass(classes.hidden);
  data.elements.$oneAsics.addClass(classes.hidden);
  data.elements.$multiFactorContainer.multiFactor('enable', multiFactorData);
};

const getRecoveryQuestions = (data) => {
  $.ajax({
    url: `${data.settings.routes.recoveryQuestions}?email=${encodeURIComponent(data.elements.$email.val())}`,
    method: 'get',
  }).done(() => {
    // Handle the response if needed
    window.location.href = `${data.settings.routes.recoveryQuestions}?email=${encodeURIComponent(data.elements.$email.val())}`;
  }).fail((response) => {
    data.elements.$feedback.authenticationFeedback('provide', response.responseText);
  });
};

/**
 * Send the authentication request
 * @param data
 * @param route
 */
const authenticate = (data, route) => {
  const ajaxData = {};

  data.elements.$form
    .serializeArray()
    .forEach((v) => {
      ajaxData[v.name] = v.name === 'password' ? btoa(v.value) : v.value.trim();
    });

  $.ajax({
    url: route,
    data: ajaxData,
    type: 'post',
  })
    .done((response) => succeedAuthentication(data, response))
    .fail((response) => {
      data.elements.$form.preventDoubleSubmission('reEnable');

      switch (response.status) {
        case 409:
          // token validation failed - redirect user to login page
          window.location.href = response.getResponseHeader('redirect');
          return;

        case 424:
          switchToMultiFactorMode(data, JSON.parse(response.responseText).data);
          return;

        case 429:
          window.location.href = `${data.settings.routes.signIn}?redirect=${window.location.href}`;
          // return;
          break;

        default:
          data.elements.$feedback.authenticationFeedback('provide', response.responseText);
      }
    });
};

/**
 * React to user's submission
 * @param data
 */
const submit = (data) => {
  data.elements.$feedback.authenticationFeedback('clear');

  if (!data.elements.$form[0].checkValidity()) {
    return;
  }

  const $action = data.elements.$action.filter(':checked');

  if ($action.is(selectors.actionSignUp)) {
    if (!data.elements.$termsCheckbox.is(':checked')) {
      data.elements.$feedback.authenticationFeedback('provide', data.settings.i18n.terms);
      return;
    }

    authenticate(data, data.settings.routes.signUp);
    return;
  }

  if ($action.is(selectors.actionSignIn)) {
    authenticate(data, data.settings.routes.signIn);
    return;
  }

  data.elements.$feedback.authenticationFeedback('provide', data.settings.i18n.emptyPasswordOption);
  data.elements.$form.preventDoubleSubmission('reEnable');
};

/**
 * Start the facebook login process
 * @param data
 */
const startFacebookLogin = (data) => {
  FB.getLoginStatus((response) => {
    let loginUrl = data.settings.routes.facebook;
    const action = data.elements.$form.attr('action');
    const redirect = data.elements.$form.data('redirect');

    if (action) {
      loginUrl += `?dest=${encodeURIComponent(action)}`;
    } else if (typeof redirect !== 'undefined') {
      loginUrl += `?dest=${encodeURIComponent(redirect)}`;
    }

    switch (response.status) {
      case 'connected':
        FB.api('/me', () => {
          sendGAEvent(data, 'Facebook');
          window.location.href = loginUrl;
        });
        return;

      case 'not_authorized':
        // the user is logged in to Facebook,
        // but has not authenticated your app
        FB.login((loginResponse) => {
          if (loginResponse.status === 'connected') {
            sendGAEvent(data, 'Facebook');
            window.location.href = loginUrl;
          }
        }, { scope: 'email, public_profile' });
        return;

      default:
        FB.login((loginResponse) => {
          if (loginResponse.status === 'connected') {
            sendGAEvent(data, 'Facebook');
            window.location.href = loginUrl;
          }
        }, { scope: 'email, public_profile' });
    }
  });
};

/**
 * Redirect to OneASICS login, add query string if it exists
 * @param data
 * @param queryString
 */
const redirectToOneAsicsLogin = (data, queryString = '') => {
  sendGAEvent(data, 'OneASICS');
  window.location.href = `${data.settings.routes.oneAsics}${queryString}`;
};

/**
 * Redirect to Runkeeper login, add query string if it exists
 * @param data
 * @param queryString
 */
const redirectToRunkeeperLogin = (data, queryString = '') => {
  sendGAEvent(data, 'Runkeeper');
  window.location.href = `${data.settings.routes.runkeeper}${queryString}`;
};

/**
 * Redirect to OneASICS, attaching dest query string if needed
 * @param data
 */
const startOneAsicsLogin = (data) => {
  const redirectString = getRedirectLocationFromForm(data);

  const queryString = [];

  if (redirectString) {
    queryString.push(`dest=${redirectString}`);
  }

  if (data.settings.oneAsicsStyle) {
    queryString.push(`oneAsicsStyle=${data.settings.oneAsicsStyle}`);
  }

  redirectToOneAsicsLogin(data, `?${queryString.join('&')}`);
};

/**
 * If OneASICS login is required, just redirect there, otherwise open the modal with the form
 * action being the button's href attribute
 * @param data
 * @param event
 */
const respondToAuthTrigger = (data, event) => {
  event.preventDefault();

  const href = $(event.currentTarget).attr('href');
  let queryString = `?dest=${href || window.location.href}`;

  if (data.settings.oneAsicsSignInRequired) {
    queryString += data.settings.oneAsicsStyle ? `&oneAsicsStyle=${data.settings.oneAsicsStyle}` : '';

    redirectToOneAsicsLogin(data, queryString);
    return;
  }

  if (data.settings.runkeeperSignInRequired) {
    redirectToRunkeeperLogin(data, queryString);
    return;
  }

  if (href) {
    data.elements.$form.attr('action', href);
  }

  data.elements.$modal.modal('show');
};

/**
 * Fires up
 * @param {HTMLElement} element Current element.
 * @param options
 */
const initialize = (element, options) => {
  const $element = $(element);

  if (getData($element)) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  const settings = $.extend(true, {}, $.fn[pluginName].defaults, options);
  const elements = getElements($element);
  const data = {
    settings,
    elements,
  };

  data.elements.$form.preventDoubleSubmission();

  data.elements.$multiFactorContainer.multiFactor({
    ...data.settings,
    callbacks: {
      error: (pluginData, $pluginElement, error) => {
        data.elements.$feedback.authenticationFeedback('provide', error);
      },
      success: (pluginData, $pluginElement, message) => {
        data.elements.$feedback.authenticationFeedback('provide', message);
      },
    },
  });

  data.elements.$feedback.authenticationFeedback();

  data.elements.$action.on('change', (event) => changeAuthenticationAction(data, event));
  data.elements.$form.on('submit', (event) => {
    if (!data.elements.$form[0].checkValidity()) {
      return;
    }

    event.preventDefault();
  });
  data.elements.$form.on('reset', () => reset(data));
  data.elements.$formSubmit.on('click', (event) => submit(data, event));
  data.elements.$facebook.on('click', () => startFacebookLogin(data));
  data.elements.$oneAsics.on('click', () => startOneAsicsLogin(data));
  data.elements.$recoveryQuestions.on('click', () => getRecoveryQuestions(data));
  data.elements.$authTrigger.on('click', (event) => respondToAuthTrigger(data, event));

  data.elements.$fieldset.prop('disabled', false);

  if (!data.elements.$otpWidgetContainer.length) {
    data.elements.$feedback.authenticationFeedback('clear');
  }

  data.elements.$container.data(pluginName, data);
};

/**
 * The plugin for jQuery
 * @param {string|object} options The method as a string or options as an object
 */
const plugin = function Plugin(options) {
  if (options === Object(options) || !options) {
    return this.each((index, element) => initialize(element, options));
  }

  return $.error(`Method ${options} does not exist in ${pluginName}`);
};

plugin.defaults = defaults;

$(() => {
  if (plugin in $.fn) {
    return;
  }

  $.extend($.fn, {
    [pluginName]: plugin,
  });
});
